/**
 * Shared RBAC permission constants and helpers.
 * Used by both backend (middleware) and frontend (conditional UI).
 */

// ============================================================================
// Role Definitions
// ============================================================================

export const USER_ROLES = ['owner', 'admin', 'manager', 'basic_user'] as const
export type UserRole = (typeof USER_ROLES)[number]

export const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  basic_user: 'Basic User',
}

/**
 * Numeric hierarchy for role comparison.
 * Higher number = more permissions.
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  owner: 4,
  admin: 3,
  manager: 2,
  basic_user: 1,
}

// ============================================================================
// Permission Definitions
// ============================================================================

export const PERMISSIONS = [
  'reviews.create',
  'reviews.edit',
  'reviews.approve',
  'reviews.archive',
  'vendors.create',
  'vendors.edit',
  'vendors.archive',
  'risks.create',
  'risks.edit',
  'risks.archive',
  'auditors.create',
  'auditors.edit',
  'auditors.publish',
  'auditors.archive',
  'bridgeLetters.extend',
  'clients.delete',
  'users.view',
  'users.manage',
] as const

export type Permission = (typeof PERMISSIONS)[number]

/**
 * Maps each permission to the roles that have it.
 * The first role listed is the minimum required role.
 */
export const PERMISSION_ROLES: Record<Permission, UserRole[]> = {
  'reviews.create': ['manager', 'admin', 'owner'],
  'reviews.edit': ['manager', 'admin', 'owner'],
  'reviews.approve': ['admin', 'owner'],
  'reviews.archive': ['admin', 'owner'],
  'vendors.create': ['manager', 'admin', 'owner'],
  'vendors.edit': ['manager', 'admin', 'owner'],
  'vendors.archive': ['admin', 'owner'],
  'risks.create': ['manager', 'admin', 'owner'],
  'risks.edit': ['manager', 'admin', 'owner'],
  'risks.archive': ['admin', 'owner'],
  'auditors.create': ['manager', 'admin', 'owner'],
  'auditors.edit': ['manager', 'admin', 'owner'],
  'auditors.publish': ['admin', 'owner'],
  'auditors.archive': ['admin', 'owner'],
  'bridgeLetters.extend': ['admin', 'owner'],
  'clients.delete': ['admin', 'owner'],
  'users.view': ['admin', 'owner'],
  'users.manage': ['admin', 'owner'],
}

// ============================================================================
// Permission Checking Functions
// ============================================================================

/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  const allowedRoles = PERMISSION_ROLES[permission]
  return allowedRoles?.includes(role) ?? false
}

/**
 * Check if a role meets or exceeds a minimum role level.
 * Uses the role hierarchy for comparison.
 */
export function hasMinRole(userRole: UserRole, minRole: UserRole): boolean {
  const userLevel = ROLE_HIERARCHY[userRole] ?? 0
  const minLevel = ROLE_HIERARCHY[minRole] ?? 0
  return userLevel >= minLevel
}

/**
 * Check if a role is valid (exists in the role list).
 */
export function isValidRole(role: string): role is UserRole {
  return USER_ROLES.includes(role as UserRole)
}

// ============================================================================
// Role Assignment Validation
// ============================================================================

/**
 * Roles that can be assigned via the application.
 * Owner role can only be set via direct database access.
 */
export const ASSIGNABLE_ROLES = ['admin', 'manager', 'basic_user'] as const
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number]

/**
 * Check if a role can be assigned via the application (not owner).
 */
export function isAssignableRole(role: string): role is AssignableRole {
  return ASSIGNABLE_ROLES.includes(role as AssignableRole)
}

/**
 * Determine what roles an actor can assign to other users.
 * - Owners can assign any role except owner
 * - Admins can assign basic_user and manager only
 * - Others cannot assign roles
 */
export function getAssignableRolesForActor(actorRole: UserRole): AssignableRole[] {
  if (actorRole === 'owner') {
    return ['admin', 'manager', 'basic_user']
  }
  if (actorRole === 'admin') {
    return ['manager', 'basic_user']
  }
  return []
}

/**
 * Check if an actor can modify a target user's role.
 * Rules:
 * - Nobody can modify an owner's role via the app
 * - Admins cannot modify other admins
 * - Admins cannot modify owners
 * - Owners can modify anyone except owners
 */
export function canModifyUserRole(actorRole: UserRole, targetRole: UserRole): boolean {
  // Cannot modify owner role via the app
  if (targetRole === 'owner') {
    return false
  }

  // Admins cannot modify other admins (owners already handled above)
  if (actorRole === 'admin' && targetRole === 'admin') {
    return false
  }

  // Only admins and owners can modify roles
  return hasPermission(actorRole, 'users.manage')
}

/**
 * Check if an actor can assign a specific role to a target user.
 * Combines role modification check with assignable roles check.
 */
export function canAssignRole(actorRole: UserRole, targetCurrentRole: UserRole, newRole: string): boolean {
  // Must be able to modify the target's role
  if (!canModifyUserRole(actorRole, targetCurrentRole)) {
    return false
  }

  // New role must be assignable
  if (!isAssignableRole(newRole)) {
    return false
  }

  // Actor must be allowed to assign this specific role
  const assignableRoles = getAssignableRolesForActor(actorRole)
  return assignableRoles.includes(newRole)
}
